/**
 * TanStack Query hooks for file metadata, permissions, listings.
 *
 * The whole point of these hooks is shared cache: if `<TabContent>` and
 * `<PermissionAwareEditor>` and the workspace store all ask for the same
 * `useFile(id)`, only one network request happens and they all re-render
 * off the same cached entry.
 *
 * Mutation invalidation patterns:
 *   - File rename → `setQueryData(fileKeys.detail(id), updated)` and
 *     `setQueryData(fileKeys.list(parentId), patch)` instead of refetching
 *     the whole tree.
 *   - File move → patch source AND destination list entries; do NOT
 *     `invalidateQueries({ queryKey: fileKeys.all })`.
 *   - Delete → patch list (filter out) + remove detail.
 *
 * The `fileKeys` factory keeps cache keys consistent across the app —
 * never write `['file', id]` inline; use `fileKeys.detail(id)`.
 */
import { useRef } from 'react'
import { QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryKey, type UseQueryOptions } from '@tanstack/react-query'
import { actingOrgEmailOf, getActiveOrgEmail, getActiveTenantId, request, isSessionEndedError } from '@papan/api-client'
import { isPdAppContainerName, ROOT, isCodeFileName } from '@papan/types'
import type { FileEvent, FileEventRow } from '@papan/realtime/core'
import { filesApi, type FileListItem, type RecentFileItem, type RecentWorkFilter, type RecentWorkPage, type RecentWorkScope } from './index'
import { appRunLogApi, type AppRunWriteSource } from './app-run-log.js'

/**
 * Identity component of cache keys: tenant + org email.
 *
 * Why list/detail/permission keys carry identity: switching identity
 * (e.g. selecting a different org email mid-session) changes what the server
 * returns for the same `parentId`. Without identity in the key, TanStack
 * Query would serve the previously-cached "wrong identity" response for the
 * 30s staleTime window. See issue #34.
 *
 * Read live each call so the key reflects the *current* identity at render
 * time. The getters are wired during render in `useTenant.tsx` so they're
 * always synchronously up-to-date.
 */
function identityKey(): ReadIdentity {
  return [getActiveTenantId(), getActiveOrgEmail()] as const
}

/** Who a read is made as — what an identity-scoped key ends with (#1210). */
export type ReadIdentity = readonly [tenantId: string | null, orgEmail: string | null]
/** The identity a request sent NOW carries (the request headers read the same getters). */
export function currentReadIdentity(): ReadIdentity {
  return identityKey()
}
export function sameIdentity(a: ReadIdentity, b: ReadIdentity): boolean {
  return a[0] === b[0] && a[1] === b[1]
}
/**
 * Is a read asked as `asked` still made as that identity `now`? The rule `isCurrentIdentity` (@papan/api-client)
 * applies: an org email of NONE ("the server picks") matches whatever the tab resolved since in the same tenant — on
 * every page load into an org the stored email lands after the first reads, and that is not a switch (round 1 review).
 * Only another tenant, or a different non-null email, is another identity.
 */
export function stillReadsAs(asked: ReadIdentity, now: ReadIdentity = currentReadIdentity()): boolean {
  return asked[0] === now[0] && (asked[1] === null || asked[1] === now[1])
}
/**
 * After the ANSWER: was `answer` read as the identity active now? As `stillReadsAs`, but a read asked with no org email
 * is accepted under the email since landed only if the server acted as that email for THIS answer (or said nothing) —
 * another tab may have changed the stored choice meanwhile (D134), and the answer then belongs to that one. Judged by
 * the answer's own echo (`actingOrgEmailOf`), never the tab's latest, which a later response can overwrite.
 */
function answerReadsAs(asked: ReadIdentity, answer: unknown, now: ReadIdentity = currentReadIdentity()): boolean {
  if (asked[1] !== null || now[1] === null) return stillReadsAs(asked, now)
  const acting = actingOrgEmailOf(answer)
  return asked[0] === now[0] && (acting === undefined || acting === now[1])
}
/** The identity an identity-scoped key (`fileKeys.list`, `fileKeys.search`) was built for: its last two elements. */
export function identityOfKey(queryKey: readonly unknown[]): ReadIdentity {
  return queryKey.slice(-2) as unknown as ReadIdentity
}
/**
 * #1210 — a read refused because the active identity is no longer the one it was asked as: sending it would read as
 * another tenant / org email and store that answer under the first one's key. No `status`, so it is never taken for a
 * refusal (403 / 404); the app's query retry skips it by name (`queryRetry`).
 */
export class IdentityChangedError extends Error {
  /** `true` when the TENANT changed (not only the org email): the ids asked about belong to another tenant, so asking
   *  them again as the current one means nothing — give up quietly (round 2 full review). */
  readonly tenantChanged: boolean
  constructor(asked?: ReadIdentity) {
    super('the active identity changed during the read')
    this.name = 'IdentityChangedError'
    this.tenantChanged = asked !== undefined && asked[0] !== currentReadIdentity()[0]
  }
}

/**
 * Run `read`, asked once more when an ORG-EMAIL change (same tenant) refused it — the new ask builds its key for the
 * identity now active. A tenant change, or a second refusal, is thrown (#1210).
 *
 * #1316 × #1210: an org-email switch ENDS the session first, so a read still in flight then fails as "session ended"
 * (never reaching the identity check) — asked once more the same way while the tenant is unchanged (the new ask runs in
 * the new session). A tenant change (or a sign-out) is thrown, as before.
 */
export async function reaskOnEmailChange<T>(read: () => Promise<T>): Promise<T> {
  const askedTenant = currentReadIdentity()[0]
  try {
    return await read()
  } catch (error) {
    if (error instanceof IdentityChangedError && !error.tenantChanged) return read()
    if (isSessionEndedError(error) && currentReadIdentity()[0] === askedTenant) return read()
    throw error
  }
}

/**
 * `setQueriesData` for a cache PATCH from an event or a mutation (#1058). Two
 * rules TanStack's own call does not keep:
 *  - an updater that changes nothing returns `undefined`, and nothing is
 *    written. TanStack writes any other result — returning `prev` stamps the
 *    cache fresh;
 *  - a cache already marked stale STAYS stale after a real patch. A write
 *    clears the mark, but the patch only fixed the one row the event carried:
 *    whatever the mark was for (a pointer row whose flags are per viewer, a
 *    resync, an ancestor moved) still needs the server, on the next read.
 */
export function patchCachedQueries<T>(
  queryClient: QueryClient,
  filters: { queryKey: QueryKey; exact?: boolean },
  updater: (prev: T | undefined) => T | undefined,
): void {
  patchCachedEntries<T>(queryClient, queryClient.getQueriesData<T>(filters), updater)
}

/** `patchCachedQueries` over entries the caller already found (one cache scan for several patches). */
export function patchCachedEntries<T>(
  queryClient: QueryClient,
  entries: ReadonlyArray<readonly [QueryKey, T | undefined]>,
  updater: (prev: T | undefined) => T | undefined,
): void {
  for (const [key, prev] of entries) {
    const next = updater(prev)
    if (next === undefined || next === prev) continue
    const wasStale = queryClient.getQueryState(key)?.isInvalidated === true
    queryClient.setQueryData<T>(key, next)
    if (wasStale) void queryClient.invalidateQueries({ queryKey: key, exact: true, refetchType: 'none' })
  }
}

/** Whether merging `fields` into `row` would change any of them. A missing key and a
 *  null are the same "no value" (#1141 I3: events now carry a loaded null the listing
 *  omits — an unchanged row must still write nothing). */
export function rowDiffers(row: object, fields: object): boolean {
  const r = row as Record<string, unknown>
  return Object.entries(fields).some(([k, v]) => (r[k] ?? null) !== (v ?? null))
}

/** Apps' run sources' last write outcomes (`app_run_writes`) — answered only for apps the caller can edit, so per
 *  identity. `counts`: the taskbar's dot for several apps; `app`: one app's settings / editor badge. */
export const appWriteFailureKeys = {
  counts: (ids: readonly string[]) => ['app-writes', 'counts', ids.join(','), ...identityKey()] as const,
  app: (id: string) => ['app-writes', 'app', id, ...identityKey()] as const,
  /** Every app-writes query — refetched after a Dismiss. */
  all: ['app-writes'] as const,
}

export const fileKeys = {
  all: ['files'] as const,
  /** Single file/folder metadata */
  detail: (id: string) => [...fileKeys.all, 'detail', id] as const,
  /** Children of a folder (parentId === null = root). Identity-scoped. */
  list: (parentId: string | null, options?: ListQueryOptions) =>
    [...fileKeys.all, 'list', parentId, options ?? {}, ...identityKey()] as const,
  /** Effective permission for a file */
  /** Per identity (#1210 I3): a listing or detail asked as one org email must not seed another's answer. */
  permission: (id: string, identity: ReadIdentity = identityKey()) => [...fileKeys.all, 'permission', id, ...identity] as const,
  /** Collab room key */
  collabRoom: (id: string) => [...fileKeys.all, 'collab-room', id] as const,
  /** Breadcrumbs */
  breadcrumbs: (id: string) => [...fileKeys.all, 'breadcrumbs', id] as const,
  /** Recursive name-search results under a root. Identity-scoped — same
   *  rationale as `list` (switching identity changes what the server
   *  returns for the same query). */
  search: (rootId: string | null, query: string) =>
    [...fileKeys.all, 'search', rootId, query, ...identityKey()] as const,
  /** Raw file content (Blob). versionId pins to a specific version. */
  content: (id: string, versionId?: string) =>
    [...fileKeys.all, 'content', id, versionId ?? 'current'] as const,
  /** Tenant-wide recently-modified files (drive Home). Identity-scoped. */
  recent: (limit: number) =>
    [...fileKeys.all, 'recent', limit, ...identityKey()] as const,
  /** #653 — Recent: grouped recent changes. `paged` = the Recent page (every page so far), `first` = one page
   *  (Home, chat home, phone). Identity-scoped. */
  recentWork: (mode: 'paged' | 'first', scope: RecentWorkScope, filter: RecentWorkFilter, limit: number) =>
    [...fileKeys.all, 'recent-work', mode, scope, filter, limit, ...identityKey()] as const,
} as const

export interface ListQueryOptions {
  limit?: number
  offset?: number
  sortBy?: 'name' | 'modified' | 'size' | 'type'
  sortDir?: 'asc' | 'desc'
  findWritable?: boolean
}

/** Files refused (403 / 404) since their last permission answer, per identity — see `readPermissionQuery`. */
const refusedSinceAnswer = new Set<string>()
/**
 * #1210 — for a permission answer read with NO org email ("the server picks"): the email the server acted as for that
 * answer, per (file, tenant) — how a no-email permission read learns which email's key it also answers for
 * (`readPermissionQuery`), never the tab's latest echo, which another tab's switch can move (round 2 full review). Bounded: the oldest entries go first past the cap (a tab
 * on a tenant whose email stays unresolved would otherwise add one per file it ever saw).
 */
const noEmailActingOf = new Map<string, string>()
const NO_EMAIL_ACTING_CAP = 2000
function recordNoEmailActing(fileId: string, identity: ReadIdentity, acting: string | undefined): void {
  if (identity[1] !== null) return
  const key = refusalKey(fileId, identity)
  noEmailActingOf.delete(key) // re-inserted last: the Map's order is the age order the cap evicts by
  if (!acting) return
  noEmailActingOf.set(key, acting)
  while (noEmailActingOf.size > NO_EMAIL_ACTING_CAP) noEmailActingOf.delete(noEmailActingOf.keys().next().value as string)
}
/** `refusedSinceAnswer`'s key: one identity's refusal is not another's (#1210 I3). */
const refusalKey = (fileId: string, identity: ReadIdentity): string => `${fileId}\u0000${identity[0] ?? ''}\u0000${identity[1] ?? ''}`

/**
 * Write a file's permission into the cache from an ANSWER that carried it (a listing, a detail) —
 * the one way to seed it: an answer also ends the file's "refused since the last answer" record
 * (`readPermissionQuery`), so the next loss of access re-reads its detail (round 24 review).
 */
export function seedPermission(
  queryClient: QueryClient,
  fileId: string,
  permission: string,
  /** `fromDetail`: the answer IS the file's detail — never re-read it (that cancelled the very fetch seeding it).
   *  `identity`: who the answer was read as (#1210 I3) — its key, never the identity active now; default: the current. */
  opts?: { fromDetail?: boolean; identity?: ReadIdentity; acting?: string },
): void {
  const identity = opts?.identity ?? currentReadIdentity()
  queryClient.setQueryData(fileKeys.permission(fileId, identity), permission)
  // `acting`: an answer read with no org email says which email the server acted as — that is also this answer's
  // identity, so the key a reader holds once the stored email lands is seeded too (page load, round 2 full review)
  if (identity[1] === null) {
    recordNoEmailActing(fileId, identity, opts?.acting)
    if (opts?.acting) queryClient.setQueryData(fileKeys.permission(fileId, [identity[0], opts.acting]), permission)
  }
  // access REGAINED, learned here: re-read the detail as `readPermissionQuery` would (#630's tab
  // comes back even when this answer beats the permission re-read — round 25 review)
  if (refusedSinceAnswer.delete(refusalKey(fileId, identity)) && !opts?.fromDetail) void queryClient.invalidateQueries({ queryKey: fileKeys.detail(fileId) })
}

/**
 * Seed `usePermission(childId)` cache from a list response. Server inlines
 * `effectivePermission` per child (override ?? parentPermission), so opening
 * any file the user just saw in a folder list is a permission-cache hit and
 * skips the per-file `/api/files/<id>/permission` round trip.
 *
 * Safe to call from inside any queryFn that fetches a list — `setQueryData`
 * is idempotent and per-key, so multiple list responses for sibling parents
 * don't collide.
 */
export function seedPermissionsFromList(
  queryClient: QueryClient,
  files: FileListItem[],
  /** who the listing was read as (#1210 I3); default: the current identity */
  identity?: ReadIdentity,
  /** the email the server acted as for a no-email listing (`actingOrgEmailOf`) */
  acting?: string,
) {
  for (const child of files) {
    if (child.effectivePermission) {
      seedPermission(queryClient, child.id, child.effectivePermission, { identity, acting })
    }
  }
}

// ─── Answers that straddle an access change (#1260) ───────────────────
// A list (or detail) read asked BEFORE an access change and answered after it
// carries the pre-change permissions: it used to seed them back over the
// invalidation (`seedPermissionsFromList`), and a re-read joined it (`fetchQuery`
// shares a running fetch) — a folder expanded as write was revoked kept looking
// writable for minutes. Every access change that touches this viewer bumps the
// generation (`noteAccessChanged`, from `FileListCacheSync`); a read that started
// under an older one seeds nothing, is marked stale when it lands, and
// `fetchAndCacheList` asks again instead of handing it out.
let accessGeneration = 0
const listGenerationOf = new WeakMap<object, number>()
/** Answers whose fetch already read past changes as often as it may (`current: false`): marked stale
 *  for the access burst, never asked again by `fetchAndCacheList` (that made 3 requests a call). */
const readsExhausted = new WeakSet<object>()

/** An access change touching this viewer arrived: reads already running predate it. */
export function noteAccessChanged(): void {
  accessGeneration++
}

/** The current access generation — capture it before a read, compare after: moved = the answer may
 *  predate an access change (for a screen acting on an answer outside the query cache). */
export function accessGenerationNow(): number {
  return accessGeneration
}

/** At most this many reads in one fetch while access changes keep arriving (#1260). */
const MAX_READS_PER_FETCH = 2

/**
 * Read, and read again while an access change came in during the read — up to `maxReads`
 * (MAX_READS_PER_FETCH). A refetch an access change starts JOINS a running fetch when the key holds
 * no data yet (TanStack cancels a running fetch only to replace data it already has), so the
 * pre-change answer would stand. `current: false` = changes kept arriving through every read — the
 * caller marks it stale and the access burst's next fire reads it (never a chain of reads here: on
 * a slow server, re-reading at once was what bypassed the 5 s throttle — full review). The query
 * functions deliberately do NOT read TanStack's abort `signal`: reading it makes a fetch cancel when
 * its last observer unmounts, failing an imperative `fetchAndCacheList` that joined it (round 15
 * review) — the read cap bounds a cancelled fetch's work instead.
 */
async function readPastAccessChanges<T>(
  read: () => Promise<T>,
  /** `identity`: the identity an identity-scoped key was built for — no read (the first included) is sent once the
   *  active identity is another (#1210: a re-read, retry or joiner would read as that one under this key). Without
   *  it (keys that carry no identity — a file's detail) each read is made as the identity active then, and the one
   *  whose answer is returned is reported (what a permission seeded from it belongs to). */
  opts: { maxReads?: number; identity?: ReadIdentity } = {},
): Promise<{ value: T; generation: number; current: boolean; identity: ReadIdentity }> {
  const maxReads = opts.maxReads ?? MAX_READS_PER_FETCH
  for (let reads = 1; ; reads++) {
    if (opts.identity && !stillReadsAs(opts.identity)) throw new IdentityChangedError(opts.identity)
    const identity = opts.identity ?? currentReadIdentity()
    const generation = accessGeneration
    const last = () => reads >= maxReads
    try {
      const value = await read()
      if (generation === accessGeneration || last()) {
        return { value, generation, current: generation === accessGeneration, identity }
      }
    } catch (error) {
      // a REFUSAL (403 / 404) asked before a change says nothing about now — ask again too. Never a
      // 429 / 5xx / network failure: that is not about access, and re-asking at once would only add
      // load to a flood (the query client's own retry already covers it).
      const status = (error as { status?: number } | null)?.status
      const refusal = status === 403 || status === 404
      if (!refusal || generation === accessGeneration || last()) throw error
    }
  }
}

/**
 * The caller's permission on `fileId` as of NOW (#1260, Codex review): read past any access change
 * that came in during the read; `null` when changes kept arriving through every read — the answer is
 * superseded, and the next change's reaction asks again. Fails as `loadPermission` does. For a
 * screen that ACTS on the answer (a workspace closing on a revoke must not close on a 403 read
 * before a re-grant).
 */
export async function loadCurrentPermission(fileId: string): Promise<string | null> {
  let lastStarted = accessGeneration
  try {
    const { value, current } = await readPastAccessChanges(() => { lastStarted = accessGeneration; return loadPermission(fileId) })
    return current ? value : null
  } catch (error) {
    // the LAST read failed, and a change came in during it (the reads ran out): superseded too — a
    // workspace must not close on a 403 asked before a re-grant (Codex review)
    const status = (error as { status?: number } | null)?.status
    if ((status === 403 || status === 404) && lastStarted !== accessGeneration) return null
    throw error
  }
}

/**
 * #1406 — THE request for `GET /api/workspaces/opens-as-project/:id` (the server's project rule):
 * `undefined` when the answer carries no boolean; rejects as `request` does (a 404 from an older server
 * without the route, a 503 for a check that failed). Every client question goes through it.
 */
export function requestOpensAsProject(folderId: string): Promise<boolean | undefined> {
  return request<{ opensAsProject?: boolean }>(`/api/workspaces/opens-as-project/${encodeURIComponent(folderId)}`)
    .then((r) => (typeof r.opensAsProject === 'boolean' ? r.opensAsProject : undefined))
}

/**
 * #1406 — does `folderId` open AS A PROJECT for the caller, as of NOW (the server's `projectOpenChecker`:
 * enter it + read its config)? Read past access changes as {@link loadCurrentPermission}: `null` when
 * the answer is superseded. `undefined` when this server has no such route (a 404 — an older server).
 * Any other failure throws: a FAILED CHECK, never a refusal (the route answers `false`, never 403/404).
 */
export async function loadCurrentOpensAsProject(folderId: string): Promise<boolean | null | undefined> {
  try {
    const { value, current } = await readPastAccessChanges(() => requestOpensAsProject(folderId))
    if (!current) return null
    return value
  } catch (error) {
    if ((error as { status?: number } | null)?.status === 404) return undefined
    throw error
  }
}

/** A list query's read: seeds the permission cache only if no access change came in between. */
async function readListQuery<T extends { files: FileListItem[] }>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  read: () => Promise<T>,
  opts: { maxReads?: number } = {},
): Promise<T> {
  const held = queryClient.getQueryState<T>(queryKey)?.data
  const hadData = held !== undefined
  let outcome: Awaited<ReturnType<typeof readPastAccessChanges<T>>>
  try {
    outcome = await readPastAccessChanges(read, { ...opts, identity: identityOfKey(queryKey) })
  } catch (error) {
    // #1210: the identity moved since this key was asked for. An observer that hasn't re-rendered since a switch still
    // refetches its old key (a burst, a focus); it must not land in an error — keep what the key holds, unchanged, and
    // mark it stale once this fetch has settled (a mark inside it is wiped by the success). Nothing is sent, written
    // or seeded as the other identity. A key with nothing to keep fails (its first load never sent).
    const latest = queryClient.getQueryState<T>(queryKey)?.data ?? held
    if (!(error instanceof IdentityChangedError) || latest === undefined) throw error
    setTimeout(() => { void queryClient.invalidateQueries({ queryKey, exact: true, refetchType: 'none' }) }, 0)
    return latest
  }
  const { value: result, generation, current, identity } = outcome
  listGenerationOf.set(result, generation)
  if (!current) readsExhausted.add(result)
  if (current) {
    seedPermissionsFromList(queryClient, result.files, identity, actingOrgEmailOf(result))
  } else {
    // Changes kept arriving through every read: stored by the caller after this returns — mark it
    // stale once it is. When the key already HELD data, the burst's last fire replaced this read
    // (TanStack cancels a running fetch that has data to replace), so marking is enough —
    // refetching here chained reads at round-trip speed through a sustained run of changes. A
    // FIRST load (no data) was JOINED by that fire instead: refetch it once, now that it holds data.
    setTimeout(() => { void queryClient.invalidateQueries({ queryKey, exact: true, refetchType: hadData ? 'none' : 'active' }) }, 0)
  }
  return result
}

/**
 * #1210 — `readListQuery`'s guarantees for an identity-scoped query that seeds nothing (search, recently modified):
 * read past an access change or resync boundary that came in during it (marked stale when they kept arriving), never
 * sent as — nor answered by — another identity, and an observer refused for an identity change keeps what its key holds
 * (marked stale once the fetch settles).
 */
export async function identityGuardedRead<T>(queryClient: QueryClient, queryKey: QueryKey, read: () => Promise<T>): Promise<T> {
  const asked = identityOfKey(queryKey)
  const held = queryClient.getQueryState<T>(queryKey)?.data
  try {
    const { value, current } = await readPastAccessChanges(read, { identity: asked })
    if (!answerReadsAs(asked, value)) throw new IdentityChangedError(asked)
    if (!current) setTimeout(() => { void queryClient.invalidateQueries({ queryKey, exact: true, refetchType: held !== undefined ? 'none' : 'active' }) }, 0)
    return value
  } catch (error) {
    const latest = queryClient.getQueryState<T>(queryKey)?.data ?? held
    if (!(error instanceof IdentityChangedError) || latest === undefined) throw error
    setTimeout(() => { void queryClient.invalidateQueries({ queryKey, exact: true, refetchType: 'none' }) }, 0)
    return latest
  }
}

// ─── The app-wide identity guard (#1210, owner 2026-10-22) ─────────────
/**
 * THE registry of identity-scoped query keys: every key built with the active (tenant, org email) in it, and where that
 * pair sits (`at`: its index, negative from the end). A query keyed by one is never run once the active identity is
 * another — an observer that hasn't re-rendered since a switch would otherwise refetch its OLD key with the NEW
 * identity's headers and store that answer under the old one's key. Pinned by `identity-scoped-keys-1210.test.ts`: a
 * new key builder that puts the identity in a key must be listed here.
 */
export const IDENTITY_SCOPED_KEYS: ReadonlyArray<{ prefix: readonly unknown[]; at: number }> = [
  { prefix: ['files', 'list'], at: -2 },
  { prefix: ['files', 'search'], at: -2 },
  { prefix: ['files', 'recent'], at: -2 },
  { prefix: ['files', 'recent-work'], at: -2 },
  { prefix: ['files', 'permission'], at: -2 },
  { prefix: ['files', 'my-space'], at: -2 },
  { prefix: ['billing', 'subscription'], at: -2 },
  { prefix: ['billing', 'invoices'], at: -2 },
  { prefix: ['billing', 'payment-method'], at: -2 },
  { prefix: ['billing', 'credits'], at: -2 },
  { prefix: ['billing', 'manual-statement'], at: -2 },
  { prefix: ['billing', 'storage'], at: -2 },
  { prefix: ['billing', 'subscription-terms'], at: -2 },
  { prefix: ['projects'], at: -2 },
  { prefix: ['shared-overview'], at: 1 },
  { prefix: ['shared-bin'], at: 1 },
  { prefix: ['org-member-names'], at: 1 },
  { prefix: ['system-workspace', 'drive'], at: 2 },
  { prefix: ['my-capabilities'], at: 1 },
  { prefix: ['launcher', 'shortcuts'], at: 2 },
  { prefix: ['mobile-workspaces'], at: 1 },
  { prefix: ['mobile-recent-workspaces'], at: 1 },
  { prefix: ['mobile-open-workspaces'], at: 1 },
  { prefix: ['app-writes', 'counts'], at: -2 },
  { prefix: ['app-writes', 'app'], at: -2 },
]

/** The (tenant, org email) an identity-scoped key was built for; `null` for any other key. */
export function keyIdentityOf(queryKey: readonly unknown[]): ReadIdentity | null {
  for (const { prefix, at } of IDENTITY_SCOPED_KEYS) {
    if (queryKey.length < prefix.length + 2 || !prefix.every((part, i) => queryKey[i] === part)) continue
    const i = at < 0 ? queryKey.length + at : at
    const tenant = queryKey[i]
    // a key built with no email spells it `null`, `''` or `undefined` — all "the server picks"
    const email = queryKey[i + 1] === '' || queryKey[i + 1] === undefined ? null : queryKey[i + 1]
    if ((tenant === null || typeof tenant === 'string') && (email === null || typeof email === 'string')) return [tenant, email]
  }
  return null
}

const guardedQueryFns = new WeakSet<object>()

/**
 * The app's QueryClient: every query whose key is identity-scoped (`IDENTITY_SCOPED_KEYS`) runs behind the identity
 * guard — not sent once the active identity is another than its key's, its answer refused when it was read as another,
 * and an observer refused that way keeps what its key holds (marked stale once the fetch settles). One place, so a new
 * identity-scoped query is covered without its author remembering. Reads that already guard themselves (lists,
 * details, permissions, search) pass through it unchanged: the same checks, the same answer.
 */
export class IdentityGuardedQueryClient extends QueryClient {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  override defaultQueryOptions(options: any): any {
    const defaulted = super.defaultQueryOptions(options)
    const queryFn = defaulted.queryFn
    const asked = keyIdentityOf(defaulted.queryKey as readonly unknown[])
    if (asked && typeof queryFn === 'function' && !guardedQueryFns.has(queryFn)) {
      const queryKey = defaulted.queryKey as QueryKey
      const run = queryFn as (context: unknown) => Promise<unknown>
      const wrapped = (context: unknown) => guardIdentityScoped(this, queryKey, asked, () => run(context))
      guardedQueryFns.add(wrapped)
      defaulted.queryFn = wrapped
    }
    return defaulted
  }
}

async function guardIdentityScoped<T>(queryClient: QueryClient, queryKey: QueryKey, asked: ReadIdentity, read: () => Promise<T>): Promise<T> {
  const held = queryClient.getQueryState<T>(queryKey)?.data
  try {
    if (!stillReadsAs(asked)) throw new IdentityChangedError(asked)
    const value = await read()
    if (!answerReadsAs(asked, value)) throw new IdentityChangedError(asked)
    return value
  } catch (error) {
    const latest = queryClient.getQueryState<T>(queryKey)?.data ?? held
    // #1316 review round 1: an answer dropped because its session ended is no answer either — the key keeps what it
    // holds (marked stale), never a cached error a view shows on coming back within the staleTime
    if (!(error instanceof IdentityChangedError || isSessionEndedError(error)) || latest === undefined) throw error
    setTimeout(() => { void queryClient.invalidateQueries({ queryKey, exact: true, refetchType: 'none' }) }, 0)
    return latest
  }
}

/**
 * Did this answer start before the latest access change? A copy written by a realtime patch
 * carries no generation and counts as current — safe, because the patch keeps the key's stale
 * mark (`patchCachedEntries`), so a stale entry is still asked again.
 */
function predatesAccessChange(result: object): boolean {
  const generation = listGenerationOf.get(result)
  return generation !== undefined && generation < accessGeneration
}

/**
 * Imperative folder-list fetch that routes through the shared TanStack Query
 * cache and seeds per-child permission cache. Use from non-render contexts
 * (picker dialogs, drag/drop helpers, useEffect bodies) where you need an
 * `await`-able result rather than the `useFilesList` hook.
 *
 * Default options match `WorkspaceExplorer` so a picker opening a folder the
 * tree already loaded is a cache hit.
 */
/**
 * How many rows one listing brings the pickers. A folder with more is shown
 * IN PART — and the response cannot always say so: the route reports
 * `hasMore: false` whenever it filtered a row out for access (`routes.ts`,
 * "the SQL-side total + hasMore no longer matches the visible list"), so a
 * full page is the only honest signal a caller has.
 */
export const PICKER_LIST_LIMIT = 500

export async function fetchAndCacheList(
  queryClient: QueryClient,
  parentId: string | null,
  options?: ListQueryOptions,
  /**
   * `fresh: true` bypasses the 30s staleTime for THIS read (the entry is still
   * written to the cache, so later readers benefit).
   *
   * For a PICKER, stale is not a minor cost: the drive's own view is corrected
   * by its realtime `file:*` patches and its local accumulator, while a dialog
   * mounts, reads whatever the cache holds, and copies it into local state — so
   * a file deleted moments ago is still offered as a choice, and picking it
   * fails against a row that no longer exists. Reported: a deleted `.chain`
   * still appearing in the chain-target picker.
   *
   * Cost is one request per folder the user actually opens in a dialog, which
   * is the trade the drive's own 30s window is not making because it has the
   * realtime channel and the dialog does not.
   *
   * The entry is still WRITTEN rather than bypassed — but note who benefits: the
   * drive reads a different key (it passes `{limit, offset, sortBy, sortDir}`
   * while the pickers take the `{name, asc, 500}` default, and `fileKeys.list`
   * includes the options), so the write serves other picker instances and
   * `FileExplorer`, never the drive. Writing is still right — a bypass would
   * make each dialog pay again — the beneficiary is just not the drive.
   *
   * `staleTime` (the trees pass 1 s, 0 for a resync's re-ask): how old a cached
   * answer may be, when not `fresh`.
   */
  opts2?: { fresh?: boolean; staleTime?: number },
) {
  const opts: ListQueryOptions = options ?? { sortBy: 'name', sortDir: 'asc', limit: PICKER_LIST_LIMIT }
  const queryKey = fileKeys.list(parentId, opts)
  const read = (staleTime: number, maxReads?: number) => queryClient.fetchQuery({
    queryKey,
    queryFn: () => readListQuery(queryClient, queryKey, () => filesApi.listChildren(parentId, opts), { maxReads }),
    staleTime,
  })
  // An answer that started before the latest access change (#1260) — a read this one joined, or
  // one that landed after the change marked the key stale — is asked again, ONCE (a single read): a
  // change that comes in during it is the burst's to refresh, never a chain of reads here.
  // Joined a read someone else started? Then its answer may be exhausted by THEIR reads, not ours.
  const joined = queryClient.getQueryState(queryKey)?.fetchStatus === 'fetching'
  const first = await read(opts2?.fresh ? 0 : opts2?.staleTime ?? 30_000)
  // …unless this call's OWN fetch already read as often as it may: it is marked stale for the access
  // burst — a third request here bypassed the throttle (full review). An answer this call merely
  // joined is asked again once: an imperative key (explorer subfolder, picker) has no observer for
  // the burst to refetch (round 18 review).
  const result = predatesAccessChange(first) && (joined || !readsExhausted.has(first)) ? await read(0, 1) : first
  // #1210: the caller (a tree, the drive, a picker) built this key for the identity active when it asked, and has no
  // identity dependency of its own — an answer for an identity that is no longer active is refused, so it asks again.
  if (!answerReadsAs(identityOfKey(queryKey), result)) throw new IdentityChangedError(identityOfKey(queryKey))
  return result
}

/**
 * Imperative recursive search through the shared cache. Mirrors
 * `fetchAndCacheList`: routes through TanStack Query (30s staleTime) and
 * seeds the per-child permission cache from the response, so selecting a
 * search result and opening it is a permission-cache hit.
 *
 * Use from the picker dialogs' search box (a non-render `await`-able
 * context). `rootId` scopes the search to a subtree; omit it to search
 * the whole drive.
 */
export async function fetchAndCacheSearch(
  queryClient: QueryClient,
  query: string,
  options?: { rootId?: string | null; limit?: number },
  /** See `fetchAndCacheList`. Search RESULTS are selectable in the pickers, so
   *  a stale hit is the same defect by another route: search, delete the hit,
   *  search the same term again inside the window, and it is still offered. */
  opts2?: { fresh?: boolean },
) {
  const rootId = options?.rootId ?? null
  const queryKey = fileKeys.search(rootId, query)
  const result = await queryClient.fetchQuery({
    queryKey,
    queryFn: () => readListQuery(queryClient, queryKey, () => filesApi.searchFiles(query, options)),
    staleTime: opts2?.fresh ? 0 : 30_000,
  })
  // #1210: as `fetchAndCacheList` — never an answer for an identity no longer active.
  if (!answerReadsAs(identityOfKey(queryKey), result)) throw new IdentityChangedError(identityOfKey(queryKey))
  return result
}

/**
 * Imperative file-detail fetch through the shared cache. Mirrors the `useFile`
 * hook: caches under `fileKeys.detail(id)` and seeds the sibling
 * `permission` and `breadcrumbs` caches from the inlined response fields.
 *
 * Use this anywhere you'd reach for `filesApi.getFileInfo(id)` from a non-
 * render context (handlers, useEffects). Repeat callers within the staleTime
 * window get the cached value back; the detail response also seeds permission
 * + breadcrumb caches for downstream `usePermission` / `useBreadcrumbs`
 * consumers, so the imperative path matches what `useFile` would have done.
 */
/**
 * A file's detail read, for the detail query: asked again past an access change that came in during
 * it (as permission and list reads are — a `.shared` row's `sourcePermission`, or a pre-grant 403,
 * read before the change must not stand: Codex review), and seeding the permission + breadcrumb
 * caches only when current. Changes through every read: marked stale once stored, for the access
 * burst (which re-reads `.shared` and failed details).
 */
async function readDetailQuery(queryClient: QueryClient, fileId: string): Promise<FileDetail> {
  const hadData = queryClient.getQueryState(fileKeys.detail(fileId))?.data !== undefined
  const { value: result, current, identity } = await readPastAccessChanges(() => filesApi.getFileInfo(fileId) as Promise<FileDetail>)
  if (current && result.effectivePermission) {
    seedPermission(queryClient, fileId, result.effectivePermission, { fromDetail: true, identity, acting: actingOrgEmailOf(result) })
  }
  if (result.breadcrumbs) {
    queryClient.setQueryData(fileKeys.breadcrumbs(fileId), result.breadcrumbs)
  }
  // a FIRST load (no data) was joined, not replaced, by the burst's fire: refetch it once stored.
  // Accepted (Codex): a detail that HELD data is only marked — the access burst re-reads `.shared` and
  // failed details within its interval (≤ 5 s), so a superseded `sourcePermission` stands at most that long.
  if (!current) setTimeout(() => { void queryClient.invalidateQueries({ queryKey: fileKeys.detail(fileId), exact: true, refetchType: hadData ? 'none' : 'active' }) }, 0)
  return result
}

/**
 * A file's detail read OUTSIDE the detail query (a probe that writes the caches itself), read past
 * any access change that came in during it. `current: false` = changes kept arriving — don't store
 * it as fresh (round 28 review: SingleFileView's ended-editor probe).
 */
export async function fetchFileInfoPastAccessChanges(fileId: string): Promise<{ info: FileDetail; current: boolean; identity: ReadIdentity }> {
  const { value, current, identity } = await readPastAccessChanges(() => filesApi.getFileInfo(fileId) as Promise<FileDetail>)
  return { info: value, current, identity }
}

export function fetchAndCacheFileInfo(queryClient: QueryClient, fileId: string) {
  return queryClient.fetchQuery({
    queryKey: fileKeys.detail(fileId),
    queryFn: () => readDetailQuery(queryClient, fileId),
    staleTime: 30_000,
  })
}

// ─── Batched permission reads (#1259) ─────────────────────────────────
// An access change re-reads every open file's permission at once: one request
// per file, per tab, passed the read rate limit (gnexis 2026-10-21). Reads asked
// within PERMISSION_BATCH_WINDOW_MS of each other go out as ONE request.
const PERMISSION_BATCH_WINDOW_MS = 10
const PERMISSION_BATCH_MAX_IDS = 100
/** Characters of ids per request — keeps the URL well under a proxy's 8 KB line. */
const PERMISSION_BATCH_MAX_CHARS = 6000
type PermissionWaiter = { resolve: (permission: string) => void; reject: (error: unknown) => void }
/** Pending reads, per identity (#1210 I3): a batch is sent only as the identity it was asked as. */
let pendingPermissionReads: Map<string, { identity: ReadIdentity; waiters: Map<string, PermissionWaiter[]> }> | null = null

/** What `getPermission` throws for an id the caller may not see. */
function notAuthorizedError(): Error {
  return Object.assign(new Error('Not found or not authorized'), { status: 403 })
}

function permissionChunks(ids: string[]): string[][] {
  const chunks: string[][] = []
  let chunk: string[] = []
  let chars = 0
  for (const id of ids) {
    if (chunk.length && (chunk.length >= PERMISSION_BATCH_MAX_IDS || chars + id.length + 1 > PERMISSION_BATCH_MAX_CHARS)) {
      chunks.push(chunk); chunk = []; chars = 0
    }
    chunk.push(id); chars += id.length + 1
  }
  if (chunk.length) chunks.push(chunk)
  return chunks
}

function flushPermissionReads(): void {
  const batches = pendingPermissionReads
  pendingPermissionReads = null
  if (!batches) return
  for (const { identity, waiters } of batches.values()) flushPermissionBatch(identity, waiters)
}

function flushPermissionBatch(identity: ReadIdentity, batch: Map<string, PermissionWaiter[]>): void {
  const settle = (id: string, read: Promise<string>) => {
    for (const w of batch.get(id) ?? []) read.then(w.resolve, w.reject)
  }
  // #1210 I3: the request headers carry the identity active when it is SENT — never send one as another.
  const asked = () => stillReadsAs(identity)
  const getOne = (id: string): Promise<string> => asked() ? filesApi.getPermission(id) : Promise.reject(new IdentityChangedError(identity))
  // every single-id path records whose answer it is (`recordNoEmailActing`)
  const getOneRecorded = (id: string): Promise<string> => {
    // the email the request is SENT with (its headers are built in this same tick): once the stored email has landed
    // a no-email batch goes out naming it, and then that is whose answer it is (round 4 full review). Sent with NO
    // email, the single route's bare string carries no stamp of its own — and the tab's echo can belong to another
    // response (Codex) — so nothing is recorded: unknown is never wrong.
    const sentAs = currentReadIdentity()[1]
    return getOne(id).then(permission => {
      recordNoEmailActing(id, identity, sentAs ?? undefined)
      return permission
    })
  }
  if (!asked()) {
    for (const id of batch.keys()) settle(id, Promise.reject(new IdentityChangedError(identity)))
    return
  }
  for (const chunk of permissionChunks([...batch.keys()])) {
    if (chunk.length === 1) { settle(chunk[0], getOneRecorded(chunk[0])); continue }
    const answer = filesApi.getPermissions(chunk)
    for (const id of chunk) {
      settle(id, answer.then(
        (map) => {
          const permission = map[id]
          if (permission == null) throw notAuthorizedError()
          recordNoEmailActing(id, identity, actingOrgEmailOf(map))
          return permission
        },
        (error: unknown) => {
          // A server without the batch route (a rolling deploy) answers it as an unknown id:
          // ask one by one, as before. A 429 / 5xx / network failure is that read's answer.
          // Accepted (Codex review): during a rolling deploy that is one request per open file —
          // what every client did before #1259 (three per file, counting the per-tab re-reads),
          // and only until both API instances run this build.
          const status = (error as { status?: number } | null)?.status
          if (status === 400 || status === 403 || status === 404 || status === 405) return getOneRecorded(id)
          throw error
        },
      ))
    }
  }
}

/**
 * The caller's effective permission on `fileId`, read together with every other
 * permission asked for in the same moment (one request). Answers and fails as
 * `filesApi.getPermission` does (a 403-status error where the caller may not see
 * it). Not cached — `usePermission` / `fetchAndCachePermission` are.
 */
export function loadPermission(
  fileId: string,
  /** who the read is asked as (#1210 I3) — refused at the flush once another identity is active */
  identity: ReadIdentity = currentReadIdentity(),
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!pendingPermissionReads) {
      pendingPermissionReads = new Map()
      setTimeout(flushPermissionReads, PERMISSION_BATCH_WINDOW_MS)
    }
    const slot = refusalKey('', identity)
    const batch = pendingPermissionReads.get(slot) ?? { identity, waiters: new Map<string, PermissionWaiter[]>() }
    const waiters = batch.waiters.get(fileId) ?? []
    waiters.push({ resolve, reject })
    batch.waiters.set(fileId, waiters)
    pendingPermissionReads.set(slot, batch)
  })
}

// `refusedSinceAnswer` (declared with `seedPermission`): the detail is re-read on the FIRST refusal
// (access lost) and on the first answer after one (regained), never per refusal — TanStack keeps the
// last data through failures, and alternating 403 / 429 under load re-read it on every 403 (rounds
// 22–24 reviews). Every answer that seeds a permission ends the record too (`seedPermission`).

/**
 * The permission query's read: batched, and when the answer CHANGED from what
 * the cache held — a level changed, access lost, or regained after a refusal —
 * the file's detail is re-read too (#1259). An access change used to re-read
 * every open file's detail as well; only a changed one needs it (a re-granted
 * tab whose detail answered 403 comes back by itself — #630).
 */
async function readPermissionQuery(queryClient: QueryClient, fileId: string, queryKey: QueryKey): Promise<string> {
  // #1210 I3: the key carries who it is read as — never the identity active now.
  const identity = identityOfKey(queryKey)
  const refusal = refusalKey(fileId, identity)
  const held = queryClient.getQueryState<string>(queryKey)?.data
  const refreshDetail = () => { void queryClient.invalidateQueries({ queryKey: fileKeys.detail(fileId) }) }
  try {
    // #1260: never an answer asked before an access change (`readPastAccessChanges`); #1210: nor one read as
    // another identity — refused before it is sent, at the batch's flush included.
    const { value: permission, current } = await readPastAccessChanges(() => loadPermission(fileId, identity), { identity })
    // a read with no org email: its answer is also the answer of the email the server acted as for it — seed that
    // key too, so the re-key when the stored email lands (every page load into an org) finds it (round 3 review)
    const acting = identity[1] === null ? noEmailActingOf.get(refusalKey(fileId, identity)) : undefined
    if (current && acting) queryClient.setQueryData(fileKeys.permission(fileId, [identity[0], acting]), permission)
    const regained = refusedSinceAnswer.delete(refusal)
    if (regained || (held !== undefined && held !== permission)) refreshDetail()
    // Changes kept arriving through every read: marked stale once stored. A key that HELD data was
    // replaced by the burst's last fire, so marking is enough; a FIRST load (no data — nor an error
    // without earlier data) was joined by it: refetch it once, now that it holds data (#1260).
    if (!current) setTimeout(() => { void queryClient.invalidateQueries({ queryKey, exact: true, refetchType: held !== undefined ? 'none' : 'active' }) }, 0)
    return permission
  } catch (error) {
    // #1210: refused for an identity change — an observer that hasn't re-rendered since a switch keeps what its key
    // holds (marked stale once this fetch settles), never an error. Nothing held: fail (its first load never sent).
    if (error instanceof IdentityChangedError) {
      const latest = queryClient.getQueryState<string>(queryKey)?.data ?? held
      if (latest === undefined) throw error
      setTimeout(() => { void queryClient.invalidateQueries({ queryKey, exact: true, refetchType: 'none' }) }, 0)
      return latest
    }
    const status = (error as { status?: number } | null)?.status
    if ((status === 403 || status === 404) && !refusedSinceAnswer.has(refusal)) {
      refusedSinceAnswer.add(refusal)
      if (held !== undefined) refreshDetail() // access just LOST
    }
    throw error
  }
}

/**
 * THE options of a `fileKeys.permission(id)` query (#1259) — every reader of that key passes
 * these: a query's refetch runs whichever observer's `queryFn` was set last, so one reader with
 * its own fetcher (the dock read `filesApi.getPermission` directly) silently un-batches every
 * refetch of that key.
 */
export function permissionQueryOptions(queryClient: QueryClient, fileId: string) {
  const queryKey = fileKeys.permission(fileId)
  return {
    queryKey,
    queryFn: () => readPermissionQuery(queryClient, fileId, queryKey),
    staleTime: 5 * 60_000,
  }
}

/**
 * Imperative permission fetch through the shared cache. Useful when you need
 * a permission lookup outside React (e.g. before deciding whether to open a
 * dialog). Reads from cache when fresh — the list-seeded permission means
 * a folder you just rendered in the tree is usually a cache hit.
 */
export function fetchAndCachePermission(queryClient: QueryClient, fileId: string) {
  return queryClient.fetchQuery(permissionQueryOptions(queryClient, fileId))
}

type FileDetail = FileListItem & {
  path: string
  /** Inlined breadcrumbs from the detail endpoint — seeds useBreadcrumbs cache. */
  breadcrumbs?: Array<{ id: string; name: string }>
}

/**
 * #1210 — a number that changes only when the ORG EMAIL changes within the tenant (a switch), never on the page-load
 * hydration (no email → the stored one) nor on a tenant change (the workspace remounts by tenant anyway). An open
 * file's content was loaded as the previous email — a PDF, an image, a grid, a version preview — and nothing re-reads
 * it; keying the open-file surfaces by this remounts them, so they load again as the identity now active (decided,
 * owner 2026-10-22: an email switch reloads open files). Read during render, like the key builders: the getters are
 * wired during render (`useTenant`).
 */
export function useIdentitySwitchEpoch(): number {
  const seen = useRef<{ last: ReadIdentity | null; epoch: number }>({ last: null, epoch: 0 })
  const now = currentReadIdentity()
  const s = seen.current
  if (now[1] !== null) {
    if (s.last && s.last[0] === now[0] && s.last[1] !== now[1]) s.epoch++
    s.last = now
  }
  return s.epoch
}

/**
 * One app's run sources' last write outcomes (its triggers, schedules and scripts) — for the editor's badge, the app
 * shell's settings gear and the settings page. Answered only to someone who can EDIT the app: anyone else gets `null`
 * (a 403 is "nothing to show", never an error on screen). Polled — a trigger's writes fail after it ran, unattended.
 */
export function useAppWrites(folderId: string | null | undefined, opts: { enabled?: boolean } = {}) {
  return useQuery<AppRunWriteSource[] | null>({
    queryKey: appWriteFailureKeys.app(folderId ?? ''),
    queryFn: () => appRunLogApi.writes(folderId!).then((r) => r.sources, (err: { status?: number }) => {
      if (err?.status === 403 || err?.status === 404) return null
      throw err
    }),
    enabled: !!folderId && (opts.enabled ?? true),
    staleTime: 30_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
    retry: false,
  })
}

/** The sources whose last run's writes failed and nobody dismissed — what every indicator counts. */
export function openWriteFailures(sources: readonly AppRunWriteSource[] | null | undefined): AppRunWriteSource[] {
  return (sources ?? []).filter((s) => s.outcome !== 'landed' && !s.dismissed)
}

/**
 * Get file/folder metadata. Cache shared across all consumers; safe to call
 * from many components for the same id without fan-out.
 *
 * Pass `enabled: false` (via `options.enabled`) to defer until you have an id.
 */
export function useFile(
  id: string | null | undefined,
  options?: Omit<UseQueryOptions<FileDetail>, 'queryKey' | 'queryFn'>,
) {
  const queryClient = useQueryClient()
  return useQuery<FileDetail>({
    queryKey: fileKeys.detail(id || ''),
    // Seeds sibling caches: permission + breadcrumbs ride along on the detail response, so
    // usePermission(id) / useBreadcrumbs(id) hit the cache on first render (`readDetailQuery` —
    // never over an access change that came in meanwhile, #1260).
    queryFn: () => readDetailQuery(queryClient, id!),
    enabled: !!id && (options?.enabled ?? true),
    ...options,
  })
}

/**
 * Get the caller's effective permission on a file. Permissions don't change
 * often; use longer staleTime than file metadata.
 */
export function usePermission(
  id: string | null | undefined,
  options?: Omit<UseQueryOptions<string>, 'queryKey' | 'queryFn'>,
) {
  const queryClient = useQueryClient()
  return useQuery<string>({
    // #1210 I3: no placeholder across the page-load re-key (tenant, none) → (tenant, email): every answer read with no
    // email also seeds the key of the email the server acted as, so that key already holds it. A real switch reloads.
    // Accepted (round 7 review): that seeded key has no observer until the re-key, so if the stored email lands more
    // than gcTime (5 min) after the last seed — or right after a `current: false` read — the editor still shows its
    // spinner once. The email lands within seconds of a page load in practice.
    ...permissionQueryOptions(queryClient, id || ''),
    enabled: !!id && (options?.enabled ?? true),
    ...options,
  })
}

/**
 * Get the collab room key for a file. Tied to the file's group membership;
 * invalidated when a workbook group is created/dissolved (see
 * `papan:collab-epoch-changed` window event handlers).
 */
export function useCollabRoom(
  id: string | null | undefined,
  options?: Omit<UseQueryOptions<{ room: string; isWorkbookGroup: boolean } | null>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<{ room: string; isWorkbookGroup: boolean } | null>({
    queryKey: fileKeys.collabRoom(id || ''),
    queryFn: () => filesApi.getCollabRoom(id!).catch(() => null),
    enabled: !!id && (options?.enabled ?? true),
    ...options,
  })
}

/**
 * Get breadcrumbs (path from root to file).
 */
export function useBreadcrumbs(
  id: string | null | undefined,
  options?: Omit<UseQueryOptions<Array<{ id: string; name: string }>>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<Array<{ id: string; name: string }>>({
    queryKey: fileKeys.breadcrumbs(id || ''),
    queryFn: () => filesApi.getBreadcrumbs(id!),
    enabled: !!id && (options?.enabled ?? true),
    ...options,
  })
}

/**
 * List children of a folder. parentId === null means drive root.
 *
 * The dedup here is what kills the move-cascade fan-out: when a file moves,
 * mutation handlers patch the source and destination list keys via
 * `queryClient.setQueryData`, no refetch needed.
 */
export type FilesListResult = {
  files: FileListItem[]
  total: number
  hasMore: boolean
  parentPermission?: string
  writableFolders?: { id: string; name?: string; parentId?: string | null; note?: string }[]
}

export function useFilesList(
  parentId: string | null,
  listOptions?: ListQueryOptions,
  options?: Omit<UseQueryOptions<FilesListResult>, 'queryKey' | 'queryFn'>,
) {
  const queryClient = useQueryClient()
  const queryKey = fileKeys.list(parentId, listOptions)
  return useQuery<FilesListResult>({
    queryKey,
    queryFn: () => readListQuery(queryClient, queryKey, () => filesApi.listChildren(parentId, listOptions)),
    ...options,
  })
}

/** Same shape as DriveShell/Home root listing — structural key equality
 *  shares the cache entry, so this adds no extra request. */
const NAV_ROOT_LIST_OPTS: ListQueryOptions = { sortBy: 'name', sortDir: 'asc', limit: PICKER_LIST_LIMIT }

/**
 * Resolve the drive nav's dynamic destinations from the (cached) root listing
 * plus a path lookup:
 *  - `libraryId` / `sharedId`: top-level root children, by reserved name.
 *  - `mySpaceId`: the org member's private `Persons/<email>` space. It's
 *    restricted + two levels deep, so it never appears in the root listing —
 *    resolve it by path (access-gated server-side). Only queried in an org
 *    with an email; null on personal tenants or when the space doesn't resolve
 *    (e.g. an un-migrated tenant still on the legacy hierarchy).
 * `enabled: false` runs neither query (a picker scoped to one folder offers no places).
 */
export function useDriveNavTargets(opts?: { isOrg?: boolean; orgEmail?: string | null; enabled?: boolean }) {
  const isOrg = !!opts?.isOrg
  const email = opts?.orgEmail ?? undefined
  const enabled = opts?.enabled ?? true
  const root = useFilesList(null, NAV_ROOT_LIST_OPTS, { enabled })
  const libraryFolder = root.data?.files.find((f) => f.isFolder && f.name === 'Library')
  const sharedFolder = root.data?.files.find((f) => f.isFolder && (f.name === 'Shared' || f.name === '_shared'))
  // Prefer the canonical `PDApps` over the legacy `MiniApps` — on a tenant that
  // holds BOTH (the collision state the container sweep skips for a human), the
  // server's findMiniAppsContainer resolves the canonical one, so the nav must
  // agree rather than pick whichever the listing returned first.
  const pdAppFolders = root.data?.files.filter((f) => f.isFolder && isPdAppContainerName(f.name)) ?? []
  const miniAppsFolder = pdAppFolders.find((f) => f.name === ROOT.MINIAPPS) ?? pdAppFolders[0]

  const mySpace = useQuery({
    queryKey: [...fileKeys.all, 'my-space', ...identityKey()] as const,
    queryFn: () => filesApi.resolvePath(`/papan/Persons/${(email ?? '').toLowerCase()}`),
    enabled: enabled && isOrg && !!email,
    staleTime: 5 * 60_000,
  })

  return {
    /** The root listing has answered (so a null `libraryId` means there is no Library). */
    rootListed: root.isSuccess || root.isError,
    libraryId: libraryFolder?.id ?? null,
    sharedId: sharedFolder?.id ?? null,
    miniAppsId: miniAppsFolder?.id ?? null,
    mySpaceId: isOrg ? (mySpace.data?.id ?? null) : null,
  }
}

/**
 * Tenant-wide recently-modified files (drive Home "Recently modified").
 * 30s staleTime like the folder lists — fresh edits surface on the next
 * visit/refetch; realtime invalidation is a possible follow-up.
 */
export function useRecentFiles(
  limit = 20,
  options?: Omit<UseQueryOptions<{ files: RecentFileItem[] }>, 'queryKey' | 'queryFn'>,
) {
  const queryClient = useQueryClient()
  const queryKey = fileKeys.recent(limit)
  return useQuery<{ files: RecentFileItem[] }>({
    queryKey,
    // #1210: identity-scoped key — never read as, or answered by, another identity
    queryFn: () => identityGuardedRead(queryClient, queryKey, () => filesApi.listRecent({ limit })),
    staleTime: 30_000,
    ...options,
  })
}

/**
 * #653 — the Recent page: grouped recent changes, paged by the server's cursor ("Show more").
 * Identity-scoped like search: the app's QueryClient guards it (`IDENTITY_SCOPED_KEYS`).
 */
export function useRecentWork(scope: RecentWorkScope, filter: RecentWorkFilter, limit = 25, opts?: {
  /** False while the page is off screen (a hidden deck): the query is neither refetched nor counted as on screen,
   *  only marked stale — it reloads when it subscribes again. */
  subscribed?: boolean
}) {
  const queryClient = useQueryClient()
  const queryKey = fileKeys.recentWork('paged', scope, filter, limit)
  // Guarded PER PAGE, here: the app QueryClient's generic guard (`guardIdentityScoped`) answers a refused read with
  // the key's whole held value — for an infinite query that is every page at once, which would be stored as ONE page.
  // A refused page answers with the page it held for the same cursor instead (or fails when it held none).
  const queryFn = async ({ pageParam }: { pageParam: string | null }): Promise<RecentWorkPage> => {
    const asked = identityOfKey(queryKey)
    try {
      if (!stillReadsAs(asked)) throw new IdentityChangedError(asked)
      const value = await filesApi.listRecentWork({ scope, filter, limit, cursor: pageParam })
      if (!answerReadsAs(asked, value)) throw new IdentityChangedError(asked)
      return value
    } catch (error) {
      const held = queryClient.getQueryData<{ pages: RecentWorkPage[]; pageParams: Array<string | null> }>(queryKey)
      const at = held ? held.pageParams.indexOf(pageParam) : -1
      if (!(error instanceof IdentityChangedError) || !held || at < 0 || !held.pages[at]) throw error
      setTimeout(() => { void queryClient.invalidateQueries({ queryKey, exact: true, refetchType: 'none' }) }, 0)
      return held.pages[at]
    }
  }
  guardedQueryFns.add(queryFn)
  return useInfiniteQuery<RecentWorkPage, Error, { pages: RecentWorkPage[] }, readonly unknown[], string | null>({
    queryKey,
    queryFn,
    initialPageParam: null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 30_000,
    subscribed: opts?.subscribed ?? true,
  })
}

/** Pages a preview reads, at most, to find something to show (a page can come back empty with a
 *  cursor when everything it scanned was hidden from the viewer). */
const RECENT_PREVIEW_MAX_PAGES = 4

/** One size for every preview, so Home, the chat home panel and the phone share ONE cached read. */
export const RECENT_PREVIEW_LIMIT = 10

/** #653 — the first groups only: Home's "Jump back in", the chat home panel, the phone's Recent strip. Pass
 *  `subscribed: false` while the surface is off screen (a hidden deck): it is then only marked stale by file events. */
export function useRecentWorkPreview(scope: RecentWorkScope, opts?: { subscribed?: boolean }) {
  const queryClient = useQueryClient()
  const limit = RECENT_PREVIEW_LIMIT
  const queryKey = fileKeys.recentWork('first', scope, 'all', limit)
  return useQuery<RecentWorkPage>({
    queryKey,
    queryFn: () => identityGuardedRead(queryClient, queryKey, async () => {
      let page = await filesApi.listRecentWork({ scope, filter: 'all', limit })
      for (let i = 1; i < RECENT_PREVIEW_MAX_PAGES && page.groups.length === 0 && page.nextCursor; i++) {
        page = await filesApi.listRecentWork({ scope, filter: 'all', limit, cursor: page.nextCursor })
      }
      return page
    }),
    staleTime: 30_000,
    subscribed: opts?.subscribed ?? true,
  })
}

/**
 * Fetch raw file content (Blob). Cached for 1 minute by default — content
 * is mostly stable, and the `file:updated` realtime event invalidates this
 * key (consumer is responsible for wiring that up; see PdfViewer for an
 * example).
 *
 * Why this matters: viewers were hammering /api/files/<id>/content multiple
 * times per open due to component remounts (key changes from sibling
 * permission/collab queries resolving). With shared cache, the second
 * mount within staleTime gets the Blob instantly and skips the fetch.
 */
export function useFileContent(
  fileId: string | null | undefined,
  versionId?: string,
  options?: Omit<UseQueryOptions<Blob>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<Blob>({
    queryKey: fileKeys.content(fileId || '', versionId),
    queryFn: () => filesApi.getFileContent(fileId!, versionId),
    enabled: !!fileId && (options?.enabled ?? true),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    ...options,
  })
}

// ─── Mutations ────────────────────────────────────────────────────────
//
// Each mutation patches the TanStack Query cache surgically on success
// rather than calling `invalidateQueries({ queryKey: fileKeys.all })`,
// which would refetch every list visible in the UI. The patch keeps the
// destination/source folder lists, file detail, and breadcrumbs in sync
// from the response payload — no extra round trips.
//
// Callers that haven't migrated to `useFilesList` yet still benefit: any
// component that *has* migrated will see the patched cache immediately.

/** Patch all relevant cache entries when a single file changes parent / name. */
function patchOnMove(
  queryClient: ReturnType<typeof useQueryClient>,
  prevFile: { id: string; parentId: string | null } | null,
  updated: FileListItem & Partial<{ path: string }>,
) {
  const oldParentId = prevFile?.parentId ?? null
  const newParentId = updated.parentId ?? null

  // 1. File detail — refresh in-place (preserves any extra fields like `path`)
  queryClient.setQueryData<FileDetail>(fileKeys.detail(updated.id), (prev) => {
    if (!prev) return undefined as unknown as FileDetail // let next read trigger fetch
    return { ...prev, ...updated }
  })

  // 2. Remove from source list (any list query keyed under oldParentId).
  //    Decrement total only when the row was actually in the cache —
  //    avoids an off-by-one if the source list wasn't populated, or if
  //    a prior delete already filtered the row out.
  if (oldParentId !== newParentId) {
    patchCachedQueries<FilesListResult>(queryClient,
      { queryKey: [...fileKeys.all, 'list', oldParentId] },
      (prev) => {
        if (!prev) return prev
        const filtered = prev.files.filter(f => f.id !== updated.id)
        if (filtered.length === prev.files.length) return prev
        return { ...prev, files: filtered, total: Math.max(0, prev.total - 1) }
      },
    )
  }

  // 3. Add to destination list (or replace existing entry on rename-only)
  patchCachedQueries<FilesListResult>(queryClient,
    { queryKey: [...fileKeys.all, 'list', newParentId] },
    (prev) => {
      if (!prev) return prev
      const existing = prev.files.findIndex(f => f.id === updated.id)
      if (existing >= 0) {
        if (!rowDiffers(prev.files[existing], updated)) return prev
        const next = [...prev.files]
        next[existing] = { ...next[existing], ...updated }
        return { ...prev, files: next }
      }
      return { ...prev, files: insertSortedRow(prev.files, updated), total: prev.total + 1 }
    },
  )

  // 4. Breadcrumbs may shift on move; safest to invalidate (cheap, only refetched if subscribed)
  queryClient.invalidateQueries({ queryKey: fileKeys.breadcrumbs(updated.id) })
}

export interface MoveFileVariables {
  fileId: string
  update: { name?: string; parentId?: string | null; confirmedAccessChange?: boolean }
  /**
   * Optional snapshot of the file before the move. When provided we can patch
   * the source folder list without a refetch. If omitted we fall back to
   * reading the cached file detail.
   */
  prevFile?: { id: string; parentId: string | null } | null
}

export function useMoveFile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ fileId, update }: MoveFileVariables) => filesApi.moveFile(fileId, update),
    onSuccess: (data, { fileId, prevFile }) => {
      if (!data.success || !data.file) return
      const fallback = queryClient.getQueryData<FileDetail>(fileKeys.detail(fileId)) ?? null
      patchOnMove(
        queryClient,
        prevFile ?? (fallback ? { id: fallback.id, parentId: fallback.parentId } : null),
        data.file,
      )
    },
  })
}

export interface CreateFolderVariables {
  parentId: string | null
  name: string
  options?: { merge?: boolean; keepBoth?: boolean }
}

export function useCreateFolder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ parentId, name, options }: CreateFolderVariables) =>
      filesApi.createFolder(parentId, name, options),
    onSuccess: (data, { parentId }) => {
      const folder = data.folder
      if (!folder) return

      // Splice into parent list under any sort/limit options
      patchCachedQueries<FilesListResult>(queryClient,
        { queryKey: [...fileKeys.all, 'list', parentId] },
        (prev) => {
          if (!prev) return prev
          // Don't dedup-add if already there (merge case can return existing folder)
          if (prev.files.some(f => f.id === folder.id)) return prev
          return { ...prev, files: [...prev.files, folder], total: prev.total + 1 }
        },
      )
    },
  })
}

// ─── Realtime patch (Phase A.2) ──────────────────────────────────────
//
// `patchListsFromFileEvent` replaces the previous `refresh()`-on-event path:
// when the server delivers a `file:*` realtime event with the affected row
// inline (Phase A.1), we splice the cache directly instead of refetching the
// whole folder list. The fallback contract is what makes this safe to roll
// out incrementally — any event that lacks enough info (older publisher, or
// a delete with no parentId hint) returns `false`, and the caller falls
// through to its existing refresh path.

/**
 * Normalise an event's parent reference to match the client cache key
 * convention (`null` = drive root). Server-side, "drive root" is a real
 * folder named `papan` whose id is `papan_<tenantId>`, but the client
 * caches list children under `parentId === null` for that root. Events
 * cross the wire with the resolved id, so we map it back here. Events
 * for non-root parents pass through unchanged.
 *
 * Returns `undefined` for missing parents (caller decides whether to bail).
 */
export function normalizeEventParentId(
  pid: string | null | undefined,
): string | null | undefined {
  if (pid === undefined || pid === null) return pid
  const tenantId = getActiveTenantId()
  if (tenantId && pid === `papan_${tenantId}`) return null
  return pid
}

/**
 * Map a wire-format `FileEventRow` into the `FileListItem` shape used by
 * cached list responses. The wire row deliberately omits per-viewer fields
 * (`effectivePermission`); they're resolved per-client at fetch time, so
 * cached entries inserted from events can be missing them. Downstream
 * `usePermission(id)` consumers will trigger a fetch when they need it.
 *
 * `parentId` is normalised to `null` when it matches the active tenant's
 * papan root, so the row sits under the same cache key the listing endpoint
 * uses for "drive root".
 */
/** A code file's content changed under a cached list (code-file approval): re-read that folder once its burst of writes
 *  settles (500 ms, as `useFiles`' fallback) — an app rewriting a `.py` repeatedly costs one refetch, not one per write. */
const codeListRefetchTimers = new WeakMap<QueryClient, Map<string, { timer: ReturnType<typeof setTimeout>; firstAt: number }>>()
function scheduleCodeListRefetch(queryClient: QueryClient, parentId: string | null): void {
  let byFolder = codeListRefetchTimers.get(queryClient)
  if (!byFolder) { byFolder = new Map(); codeListRefetchTimers.set(queryClient, byFolder) }
  const key = parentId ?? ''
  const prev = byFolder.get(key)
  if (prev) clearTimeout(prev.timer)
  const firstAt = prev?.firstAt ?? Date.now()
  // …but at most 2 s after the first write of a burst: an app writing faster than every 500 ms still gets its list re-read
  const delay = Math.max(0, Math.min(500, firstAt + 2_000 - Date.now()))
  const folders = byFolder
  folders.set(key, { firstAt, timer: setTimeout(() => {
    folders.delete(key)
    void queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list', parentId] })
  }, delay) })
}

function eventRowToListItem(row: FileEventRow): FileListItem {
  return {
    id: row.id,
    parentId: (normalizeEventParentId(row.parentId) ?? null) as string | null,
    name: row.name,
    isFolder: row.isFolder,
    contentHash: row.contentHash,
    size: row.size,
    mimeType: row.mimeType,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
    ...(row.permission ? { permission: row.permission } : {}),
    ...(row.fileType ? { fileType: row.fileType } : {}),
    ...(row.sharedFromName ? { sharedFromName: row.sharedFromName } : {}),
    // Carry metadata_json so a live-patched `.miniapp` folder shows its custom
    // icon (miniappIcon key) without a refetch (Phase 2 icon plumbing).
    // A present null means CLEARED (#1141 I3); an absent key means "not sent".
    ...(row.metadataJson !== undefined ? { metadataJson: row.metadataJson } : {}),
    // #781: always SET, never omitted — `file:updated` MERGES into the cached
    // row, so an absent key would leave a released folder on "Deleting…".
    deleting: row.deleting === true,
  }
}

/**
 * Apply a realtime `file:*` event to the TanStack Query cache. Returns
 * `true` when the cache was patched (caller can skip refetch), `false` when
 * the event lacked enough info to patch safely (caller must fall back to
 * `refresh()`).
 *
 * Why not always patch: events can arrive from publishers that haven't been
 * upgraded to carry `event.file` yet (Phase A.1 is rolling out in stages).
 * Returning `false` keeps the existing refetch path correct for those.
 *
 * For `file:deleted`, `event.file` isn't required — only `fileId` and
 * `parentId` are needed. If `parentId` is missing too, we bail and let the
 * caller refresh (matching every list under unknown parents would be both
 * expensive and wrong if a per-list view shouldn't see the row).
 */
/**
 * Insert a row into a cached list at its sorted position (folders first, then
 * name A→Z, case-insensitive — the default listing order). Realtime patches used
 * to append at the end, so a moved-in/created row (and its `_debug` folder)
 * showed out of order until a refetch. Idempotent: replaces an existing entry.
 * Non-default sorts (modified/size/desc) still settle on the next refetch.
 */
function insertSortedRow<T extends { id: string; name: string; isFolder: boolean }>(files: T[], row: T): T[] {
  const withoutRow = files.filter(f => f.id !== row.id)
  const rank = (f: T): [number, string] => [f.isFolder ? 0 : 1, f.name.toLowerCase()]
  const [rF, rN] = rank(row)
  const idx = withoutRow.findIndex(f => { const [fF, fN] = rank(f); return fF > rF || (fF === rF && fN > rN) })
  if (idx < 0) withoutRow.push(row)
  else withoutRow.splice(idx, 0, row)
  return withoutRow
}

/**
 * Whether `patchListsFromFileEvent` can apply this event, without applying it.
 * `FileListCacheSync` is the one subscriber that patches the cache (#956);
 * `useFiles` asks this to decide between splicing the event into its open
 * folder and refetching that folder.
 */
export function canPatchFileEvent(event: FileEvent): boolean {
  // #157 2nd re-review: a `.shared`/`.chain` pointer's listing flags
  // (shareBroken / shareNoAccess / alreadyShared / chainBroken) AND its
  // effectivePermission are computed PER-RECIPIENT by the mount resolver — they
  // can't ride a single broadcast payload. Splicing the bare event row would
  // render a broken/conflicting/no-access pointer as a HEALTHY row until a
  // refetch. Skip the surgical patch for a pointer row (except a delete, which
  // just removes it) so the caller recomputes it server-side, correctly
  // flagged from the start. Plain files/folders keep the fast patch path.
  if (
    event.type !== 'file:deleted'
    && (event.file?.fileType === 'shared' || event.file?.fileType === 'chain')
  ) {
    return false
  }
  switch (event.type) {
    case 'file:created':
    case 'file:restored':
    case 'file:updated':
    case 'file:moved':
      return !!event.file
    case 'file:deleted':
      // `parentId` is required to know which list to evict from.
      return event.parentId !== undefined
    default:
      return false
  }
}

/*
 * Every list write goes through `patchCachedQueries` (#1058): an updater that
 * changes nothing writes nothing, and a list already marked stale stays stale.
 */
/**
 * #1264 — every folder a cached LIST shows `fileId` in (`null` = the drive root; empty when none
 * does — a stale list of an old parent after a lost move included). Used to re-read just those
 * folders when an action on the row is refused (`onListedRowRefused`).
 */
export function findCachedListParents(queryClient: QueryClient, fileId: string): Set<string | null> {
  const parents = new Set<string | null>()
  for (const [key, data] of queryClient.getQueriesData<FilesListResult>({ queryKey: [...fileKeys.all, 'list'] })) {
    if (data?.files?.some((f) => f.id === fileId)) parents.add((key[2] as string | null | undefined) ?? null)
  }
  return parents
}

export function patchListsFromFileEvent(
  queryClient: QueryClient,
  event: FileEvent,
): boolean {
  if (!canPatchFileEvent(event)) return false
  // Normalise event parent ids to the cache convention (null = drive root)
  // exactly once at the entry point so the per-type branches don't need to
  // re-derive them.
  const newParent = normalizeEventParentId(event.parentId) ?? null
  const oldParent = normalizeEventParentId(event.oldParentId) ?? null

  switch (event.type) {
    case 'file:created':
    case 'file:restored': {
      if (!event.file) return false
      const row = eventRowToListItem(event.file)
      // `event.parentId !== undefined` distinguishes "server explicitly set
      // parent (incl. null for drive root)" from "server omitted it — fall
      // back to the row's own parentId, which `eventRowToListItem` already
      // normalised." Both branches end up with a normalised value; the
      // check exists because `newParent` can't tell those two cases apart
      // on its own (both yield null after the `?? null`).
      //
      // R20 #137 — on the wrapper publish path (`publishFileEventNow`),
      // R19 #135 added `parentId: payload.parentId ?? payload.file?.parentId`
      // so `event.parentId` is now always defined whenever `event.file` is
      // present. That means the else branch is unreachable for events from
      // the wrapper. The branch is kept for two reasons: (1) DIRECT-EVENTBUS
      // call sites (e.g. `materialize.ts:638`, `revoke-realtime.ts:38`)
      // bypass the wrapper and may still emit `parentId: undefined` with
      // `file: row`; (2) legacy / non-wrapper consumers that consume the
      // local event handler bus directly. Both branches yield the same
      // normalised value, so the ternary is safe but no longer strictly
      // needed for wrapper-routed events.
      const parentId = event.parentId !== undefined ? newParent : (row.parentId ?? null)
      // a new code file's approval state rides only on the listing (the event doesn't carry it): re-read that folder
      if (!row.isFolder && isCodeFileName(row.name)) scheduleCodeListRefetch(queryClient, parentId)
      // Splice into parent list (idempotent — replace if already present).
      // Inserted in sorted position (folders-first, name A→Z) so a moved-in /
      // created row — and its `_debug` folder — reads correctly on a folder the
      // user navigates INTO (which serves this patched cache). Non-default sorts
      // settle on the next refetch.
      patchCachedQueries<FilesListResult>(queryClient,
        { queryKey: [...fileKeys.all, 'list', parentId] },
        (prev) => {
          if (!prev) return prev
          const i = prev.files.findIndex(f => f.id === row.id)
          if (i >= 0) {
            if (!rowDiffers(prev.files[i], row)) return prev
            const next = [...prev.files]
            next[i] = { ...next[i], ...row }
            return { ...prev, files: next }
          }
          return { ...prev, files: insertSortedRow(prev.files, row), total: prev.total + 1 }
        },
      )
      return true
    }

    case 'file:updated': {
      if (!event.file) return false
      const row = eventRowToListItem(event.file)
      // See comment above on the same ternary in the file:created branch
      // (incl. the R20 #137 note about wrapper-routed events).
      const parentId = event.parentId !== undefined ? newParent : (row.parentId ?? null)
      let codeChanged = false
      patchCachedQueries<FilesListResult>(queryClient,
        { queryKey: [...fileKeys.all, 'list', parentId] },
        (prev) => {
          if (!prev) return prev
          const i = prev.files.findIndex(f => f.id === row.id)
          if (i < 0 || !rowDiffers(prev.files[i], row)) return prev // unchanged: nothing written
          const next = [...prev.files]
          // A code file's new content has a new author the event doesn't carry (code-file approval): its Approve is
          // switched OFF and the list re-read — never a click that approves bytes this row didn't list, and a file the
          // app just rewrote shows its "Needs approval" badge without waiting for the list to go stale.
          const old = prev.files[i] as FileListItem
          codeChanged ||= !row.isFolder && isCodeFileName(row.name) && old.contentHash !== row.contentHash
          next[i] = { ...next[i], ...row, ...(codeChanged ? { codeCanApprove: false } : {}) }
          return { ...prev, files: next }
        },
      )
      if (codeChanged) scheduleCodeListRefetch(queryClient, parentId)
      // Refresh detail cache only if it's currently cached — don't seed
      // from an event payload that lacks `path` and `breadcrumbs`.
      queryClient.setQueryData<FileDetail>(fileKeys.detail(row.id), (prev) =>
        prev ? { ...prev, ...row } : prev,
      )
      return true
    }

    case 'file:moved': {
      if (!event.file) return false
      const row = eventRowToListItem(event.file)
      // #662 follow-move: a SYNC-DETECTED agent `mv` mints a NEW row at the
      // destination (its own `file:created` already spliced it in) and reports
      // the move with `previousFileId` = the OLD, now-binned id. `patchOnMove`
      // keys eviction on `row.id` (the NEW id), so it would leave the old row
      // behind — a visible duplicate. Evict the old id explicitly from its
      // source list + detail, then ensure the new row is present.
      if (event.previousFileId && event.previousFileId !== row.id) {
        patchCachedQueries<FilesListResult>(queryClient,
          { queryKey: [...fileKeys.all, 'list', oldParent] },
          (prev) => {
            if (!prev) return prev
            const filtered = prev.files.filter(f => f.id !== event.previousFileId)
            if (filtered.length === prev.files.length) return undefined
            return { ...prev, files: filtered, total: Math.max(0, prev.total - 1) }
          },
        )
        queryClient.removeQueries({ queryKey: fileKeys.detail(event.previousFileId), exact: true })
        // Ensure the new row sits in the destination list (idempotent — the
        // companion file:created usually placed it already).
        patchCachedQueries<FilesListResult>(queryClient,
          { queryKey: [...fileKeys.all, 'list', newParent] },
          (prev) => {
            if (!prev) return prev
            if (prev.files.some(f => f.id === row.id)) return undefined
            return { ...prev, files: insertSortedRow(prev.files, row), total: prev.total + 1 }
          },
        )
        return true
      }
      patchOnMove(queryClient, { id: row.id, parentId: oldParent }, row)
      return true
    }

    case 'file:deleted': {
      // `parentId` is required to know which list to evict from.
      if (event.parentId === undefined) return false
      // Decrement total only when the row was actually in the cache —
      // a same-tab delete (filesApi.deleteFile + refresh()) would have
      // already removed the row, and the realtime echo for that delete
      // shouldn't double-decrement.
      patchCachedQueries<FilesListResult>(queryClient,
        { queryKey: [...fileKeys.all, 'list', newParent] },
        (prev) => {
          if (!prev) return prev
          const filtered = prev.files.filter(f => f.id !== event.fileId)
          if (filtered.length === prev.files.length) return undefined
          return { ...prev, files: filtered, total: Math.max(0, prev.total - 1) }
        },
      )
      queryClient.removeQueries({ queryKey: fileKeys.detail(event.fileId), exact: true })
      return true
    }

    default:
      return false
  }
}
